// Centralized analytics utility for GA4, designed to be lightweight and resilient
// - Buffers events until gtag is available
// - Adds common params (tenant, vendor_id)
// - Exposes pageView and event helpers

type CommonContext = {
  tenant?: string
  vendorId?: string
}

let context: CommonContext = {}
let isInitialized = false
let buffer: Array<() => void> = []
let cachedDeviceType: 'mobile' | 'tablet' | 'desktop' | null = null

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: any[]) => void
    __GA_MEASUREMENT_ID__?: string
  }
}

function callGtag(...args: any[]) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag.apply(null, args as any)
    return
  }
  buffer.push(() => callGtag(...args))
}

function flushBuffer() {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
  const q = buffer.slice()
  buffer = []
  for (const fn of q) fn()
}

function ensureGtagScriptLoaded(measurementId: string) {
  if (typeof document === 'undefined') return
  const already = Array.from(document.getElementsByTagName('script')).some(s =>
    typeof s.src === 'string' && s.src.includes('googletagmanager.com/gtag/js')
  )
  if (already) {
    // If the script tag exists, attempt to flush any buffered events
    setTimeout(flushBuffer, 0)
    return
  }
  const idle = (window as any).requestIdleCallback || ((cb: any) => setTimeout(cb, 1200))
  idle(() => {
    const s = document.createElement('script')
    s.async = true
    s.src = 'https://www.googletagmanager.com/gtag/js?id=' + measurementId
    s.onload = flushBuffer
    document.head.appendChild(s)
  })
}

export function initAnalytics(args: { measurementId?: string; tenant?: string; vendorId?: string }) {
  if (isInitialized) return
  const measurementId = args.measurementId || import.meta.env.VITE_GA_ID
  context = { tenant: args.tenant, vendorId: args.vendorId }

  // Expose to index.html stub loader
  if (typeof window !== 'undefined') {
    window.__GA_MEASUREMENT_ID__ = measurementId
  }

  // Safety: if index.html stub didn't run for some reason, set up minimal stub here and load script on idle
  if (typeof window !== 'undefined' && !window.dataLayer) {
    window.dataLayer = []
    ;(window as any).gtag = function(){ (window.dataLayer as unknown[]).push(arguments) }
    // Default: grant globally, then override to denied in EEA+UK
    callGtag('consent', 'default', { ad_storage: 'granted', analytics_storage: 'granted' })
    callGtag(
      'consent',
      'default',
      { ad_storage: 'denied', analytics_storage: 'denied', wait_for_update: 500 },
      {
        region: [
          'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE',
          'IS','LI','NO','GB'
        ]
      }
    )
    callGtag('js', new Date())
    callGtag('config', measurementId, { cookie_domain: '.tripz.live', send_page_view: false })
    ensureGtagScriptLoaded(measurementId)
  } else {
    // If stub exists, still ensure config for correct measurementId, then flush when ready
    callGtag('config', measurementId, { cookie_domain: '.tripz.live', send_page_view: false })
    ensureGtagScriptLoaded(measurementId)
  }

  isInitialized = true
}

function withCommonParams<T extends Record<string, unknown>>(params?: T): T {
  const base = params ? { ...params } : ({} as any)
  if (context.tenant) (base as any).tenant = context.tenant
  if (context.vendorId) (base as any).vendor_id = context.vendorId
  return base
}

export function pageView(params?: { page_type?: string; page_path?: string; page_title?: string }) {
  callGtag('event', 'page_view', withCommonParams(params))
}

export function event(name: string, params?: Record<string, unknown>) {
  callGtag('event', name, withCommonParams(params))
}

export function setConsentDefaultDenied() {
  callGtag('consent', 'default', { ad_storage: 'denied', analytics_storage: 'denied', wait_for_update: 500 })
}

export function updateConsentOnAccept() {
  callGtag('consent', 'update', { ad_storage: 'granted', analytics_storage: 'granted' })
}

/**
 * Detects device type based on user agent string (cached after first call)
 * Returns 'mobile', 'tablet', or 'desktop'
 * SSR-safe: returns 'desktop' on server, detects once on client
 */
function detectDeviceTypeInternal(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'desktop' // Default fallback for SSR
  }

  const userAgent = navigator.userAgent.toLowerCase()
  const isMobile = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent)
  const isTablet = /ipad|android(?!.*mobile)|tablet|playbook|silk/i.test(userAgent)

  if (isTablet) {
    return 'tablet'
  }
  if (isMobile) {
    return 'mobile'
  }
  return 'desktop'
}

/**
 * Gets cached device type (detects once on first call, then cached)
 * SSR-safe: returns 'desktop' on server
 */
export function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  if (cachedDeviceType === null) {
    cachedDeviceType = detectDeviceTypeInternal()
  }
  return cachedDeviceType
}

/**
 * Gets current page info (path, URL, timestamp) - client-side only
 * Returns null on server (SSR-safe)
 */
export function getPageInfo(): { pagePath: string; fullUrl: string; timestamp: string } | null {
  if (typeof window === 'undefined') {
    return null // SSR: return null, tracking will be skipped
  }
  return {
    pagePath: window.location.pathname,
    fullUrl: window.location.href,
    timestamp: new Date().toISOString(),
  }
}